import type { ReactNode } from "react";
import type { DataTablePaginationProps } from "./tableTypes";
import type { DataTableLabels } from "./tableLabels";
import { DataTablePagination } from "./DataTablePagination";
import { useHorizontalTrackpadScroll } from "./useHorizontalTrackpadScroll";
import { Alert, Button, Card, Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";

export type DataTableLoadState =
  | { status: "loading" }
  | {
      status: "error";
      message: string;
      retryLabel: string;
      onRetry: () => void;
    };

type DataTableShellBaseProps = {
  labels: DataTableLabels;
  loadingLabel: string;
  showPagination: boolean;
  paginationProps: DataTablePaginationProps;
  /**
   * Right end of the header row. It joins a row that already exists (title,
   * actions, toolbar or pagination) and never opens one on its own.
   */
  preferencesSlot: ReactNode;
  /** Left of the header row: the section heading, so the table needs no separate header. */
  title?: ReactNode;
  /** Right of the header row, before preferences (search, primary action…). */
  actions?: ReactNode;
  toolbar?: ReactNode;
  /**
   * Cap height and allow vertical scroll (drag-reorder lists: auto-scroll
   * stays in this nested scroller instead of the page).
   */
  constrainVerticalScroll?: boolean;
  children: ReactNode;
};

export type DataTableShellProps = DataTableShellBaseProps & {
  /** Absent = data ready (success). */
  loadState?: DataTableLoadState;
};

const BAR_CLASS =
  "flex flex-wrap items-center gap-3 border-b border-surface-200 bg-surface-0 px-4 py-2.5 text-[13px] text-surface-600";
const TRAILING_CLASS =
  "ml-auto flex max-w-full flex-wrap items-center justify-end gap-2";
const TOOLBAR_ROW_CLASS =
  "flex flex-wrap items-center gap-3 border-b border-surface-200 bg-surface-50/60 px-4 py-2.5 text-[13px] text-surface-600";

function LoadStateBody({
  loadingLabel,
  loadState,
}: {
  loadingLabel: string;
  loadState: DataTableLoadState;
}) {
  if (loadState.status === "loading") {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" loadingLabel={loadingLabel} />
      </div>
    );
  }
  return (
    <Alert
      variant="error"
      action={
        <Button variant="ghost" size="sm" onClick={loadState.onRetry}>
          {loadState.retryLabel}
        </Button>
      }
    >
      {loadState.message}
    </Alert>
  );
}

export function DataTableShell(props: DataTableShellProps) {
  const {
    labels,
    loadingLabel,
    loadState,
    showPagination,
    paginationProps,
    preferencesSlot,
    title,
    actions,
    toolbar,
    constrainVerticalScroll = false,
    children,
  } = props;
  const scrollRef = useHorizontalTrackpadScroll<HTMLDivElement>();

  const titleBlock = title ? (
    // Basis lets the actions wrap below the title on narrow cards instead of squeezing it.
    <div className="min-w-0 flex-[1_1_16rem]">{title}</div>
  ) : null;
  const trailing = (
    <div className={TRAILING_CLASS}>
      {actions}
      {preferencesSlot}
    </div>
  );

  if (loadState) {
    if (!title) {
      return <LoadStateBody loadingLabel={loadingLabel} loadState={loadState} />;
    }
    // Keep the heading in place while the rows load or fail.
    return (
      <Card className="overflow-hidden p-0 shadow-card">
        <div className={BAR_CLASS}>
          {titleBlock}
          {actions ? <div className={TRAILING_CLASS}>{actions}</div> : null}
        </div>
        <div className={loadState.status === "error" ? "p-4" : undefined}>
          <LoadStateBody loadingLabel={loadingLabel} loadState={loadState} />
        </div>
      </Card>
    );
  }

  // With a title the header row is title + actions; the toolbar gets its own row.
  const toolbarOnOwnRow = toolbar != null && (showPagination || title != null);
  const hasHeaderContent = Boolean(title || actions || toolbar);

  return (
    <Card className="overflow-hidden p-0 shadow-card">
      {showPagination ? (
        <DataTablePagination
          labels={labels}
          placement="top"
          {...paginationProps}
          leading={titleBlock}
          trailing={trailing}
        />
      ) : hasHeaderContent ? (
        <div className={BAR_CLASS}>
          {titleBlock}
          {toolbar && !toolbarOnOwnRow ? (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              {toolbar}
            </div>
          ) : null}
          {trailing}
        </div>
      ) : null}
      {toolbarOnOwnRow ? (
        <div className={TOOLBAR_ROW_CLASS}>{toolbar}</div>
      ) : null}
      <div
        ref={scrollRef}
        className={cn(
          "group/table-scroll w-full overflow-x-auto overscroll-x-contain",
          constrainVerticalScroll &&
            "max-h-[min(70vh,40rem)] overflow-y-auto overscroll-y-contain",
        )}
      >
        {children}
      </div>
      {showPagination ? (
        <DataTablePagination
          labels={labels}
          placement="bottom"
          {...paginationProps}
        />
      ) : null}
    </Card>
  );
}
